/**
 * AudioEngine.js — Section 5.1
 *
 * Creates and manages the single shared AudioContext.
 * Owns the persistent live mix graph that all sources route into.
 * The AudioContext is created lazily on first user interaction
 * to comply with browser autoplay policies.
 */

import { buildMixGraph } from './mixGraph';

let audioContext = null;
let mixGraph = null;

/**
 * Get or create the shared AudioContext.
 * Must be called from a user-gesture handler the first time.
 */
export function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    mixGraph = buildMixGraph(audioContext, { tracks: [], bpm: 90, destination: audioContext.destination });
  }
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

/**
 * Get the master GainNode. All track chains connect here.
 */
export function getMasterGain() {
  if (!mixGraph) {
    getAudioContext();
  }
  return mixGraph.masterInput;
}

export function getMixGraph() {
  if (!mixGraph) getAudioContext();
  return mixGraph;
}

export function updateMixGraph(tracks, bpm) {
  if (!mixGraph) return null;
  mixGraph.update(tracks, bpm);
  return mixGraph;
}

export function getTrackInput(trackId) { return getMixGraph().trackInput(trackId); }
export function getTrackAnalyser(trackId) { return getMixGraph().analyser(trackId); }
export function getMasterAnalyser() { return getMixGraph().masterAnalyser; }

/**
 * Set master volume (0–1).
 */
export function setMasterVolume(value) {
  const gain = getMasterGain();
  gain.gain.setValueAtTime(Math.max(0, Math.min(1, value)), getAudioContext().currentTime);
}

/**
 * Get current AudioContext time (the master clock).
 */
export function getCurrentTime() {
  if (!audioContext) return 0;
  return audioContext.currentTime;
}

/**
 * Calculate seconds per bar for a given BPM (assumes 4/4 time).
 * secondsPerBar = (60 / bpm) * 4
 */
export function secondsPerBar(bpm) {
  return (60 / bpm) * 4;
}

/**
 * Close the AudioContext (cleanup).
 */
export async function closeAudioContext() {
  if (audioContext) {
    await audioContext.close();
    audioContext = null;
    mixGraph?.dispose();
    mixGraph = null;
  }
}
